import { PanelView, SceneView } from '@rendering/lookup';
import { buildRenderingUrl } from '@rendering/product-preview';
import {
  BookletPreviewPage,
  BookletPreviewType,
  PreviewLabel,
  PreviewLayoutConfig,
  PreviewStructureItemType,
} from './types';
import { DEFAULT_PREVIEW_WIDTH_REQUEST, REQUESTOR } from '../Constants';
import { text } from '../localizationUtil';
import { getTenant } from '../tenantUtil';

const LABEL_MAP: Record<string, string> = {
  spine: 'spine-label',
  front_cover: 'front-cover-label',
  back_cover: 'back-cover-label',
  inside_front_cover: 'inside-front-cover-label',
  inside_back_cover: 'inside-back-cover-label',
  page: 'preview-page-number-label',
};

// Normalizes negative indices to positive ones
function normalizeIndex(index: number, length: number) {
  return index < 0 ? length + index : index;
}

function getPreviewType(labels: string[] | undefined): BookletPreviewType {
  const isFrontCover = labels?.includes(PreviewLabel.FrontCover) || false;
  const isBackCover = labels?.includes(PreviewLabel.BackCover) || false;

  if (isFrontCover && isBackCover) return BookletPreviewType.FullCover;
  if (isFrontCover) return BookletPreviewType.FrontCover;
  if (isBackCover) return BookletPreviewType.BackCover;
  return BookletPreviewType.Page;
}

function buildPreviewPage(
  cimDocUrl: string,
  sceneView: SceneView | undefined,
  panelViews: PanelView[],
  labels: string[] | undefined,
  type: BookletPreviewType,
  currentInnerPageRef: { value: number }
): BookletPreviewPage {
  const baseOverrides: { [key: number]: number } = {};
  panelViews.forEach((cv, index) => {
    baseOverrides[cv.panel.page] = index + 1;
  });

  // Page numbers as per labels (for inner pages)
  const pageNumbersByLabels: number[] = [];

  // build preview url
  const previewUrl = buildRenderingUrl({
    cimDocUrl,
    referer: REQUESTOR,
    tenantId: getTenant(),
    view: sceneView ?? panelViews[0],
    width: DEFAULT_PREVIEW_WIDTH_REQUEST,
    format: 'png',
    slotNumberOverrides: baseOverrides,
  });

  // handle labels and page numbers
  const resolvedLabels =
    labels?.map(label => {
      const key = LABEL_MAP[label];
      // Handle known mappings (front_cover, spine, etc.)
      if (key && key !== LABEL_MAP.page) {
        return text(key) ?? '';
      }
      const pageNumber = ++currentInnerPageRef.value;
      pageNumbersByLabels.push(pageNumber);
      // Fallback: treat as page
      return text(LABEL_MAP.page, { page: pageNumber });
    }) ?? [];

  return { previewUrl, labels: resolvedLabels, type, pageNumbersByLabels };
}

// Generates preview URLs based on the scene views, panel views, CIM document URL, and product information
// Returns an array of preview URLs
export function generateBookletPreviews(
  sceneViews: SceneView[],
  panelViews: PanelView[],
  cimDocUrl: string,
  previewLayout?: PreviewLayoutConfig
): BookletPreviewPage[] {
  if (!cimDocUrl || !previewLayout || sceneViews.length === 0 || panelViews.length === 0) {
    return [];
  }
  const previewPages: BookletPreviewPage[] = [];

  panelViews = panelViews.sort((a, b) => a.panel.page - b.panel.page);

  const { scenes, orderedPreviewStructure } = previewLayout;
  const currentInnerPageRef = {
    value: 0,
  }; // To track the current inner page for labeling

  // Iterate through the structure items to build preview URLs
  for (const mapItem of orderedPreviewStructure) {
    const { scene } = mapItem;
    const sceneView = scene ? sceneViews.find(view => view.name === scenes[scene]?.scene_name) : undefined;
    if (mapItem.type === PreviewStructureItemType.Fixed && mapItem.panels.length) {
      const panelViewsSubset: PanelView[] = mapItem.panels.map(panelIndex => {
        return panelViews[panelIndex < 0 ? panelViews.length + panelIndex : panelIndex];
      });

      const type = getPreviewType(mapItem.labels);

      const previewPage = buildPreviewPage(
        cimDocUrl,
        sceneView,
        panelViewsSubset,
        mapItem.labels,
        type,
        currentInnerPageRef
      );
      previewPages.push(previewPage);
    } else if (mapItem.type === PreviewStructureItemType.Range) {
      let [start, end] = mapItem.panel_range;
      start = normalizeIndex(start, panelViews.length);
      end = normalizeIndex(end, panelViews.length);

      if (start < 0 || end < 0 || start > panelViews.length || end > panelViews.length || start > end) {
        continue;
      }

      const type = getPreviewType(mapItem.labels);

      let currentPanelIndex = start;

      while (currentPanelIndex <= end) {
        const panelViewsSubset: PanelView[] = [];
        for (let i = 0; i < mapItem.count && currentPanelIndex <= end; i++) {
          panelViewsSubset.push(panelViews[currentPanelIndex]);
          currentPanelIndex++;
        }
        const previewPage = buildPreviewPage(
          cimDocUrl,
          sceneView,
          panelViewsSubset,
          mapItem.labels,
          type,
          currentInnerPageRef
        );
        previewPages.push(previewPage);
      }
    }
  }
  return previewPages;
}
